/* eslint-disable import/no-extraneous-dependencies */
import { BRAND, INTERNAL_DOMAINS, PROTOCOL_SCHEMA, PORT, BACKEND_PORT } from 'utils/settings'
import axios from 'axios'
import { sites } from '../i18n'
import { NextRouter } from 'next/router'

export const PAGEBUILDER_ADMIN_ENABLE = process.env.PAGEBUILDER_ADMIN_ENABLE === 'true'
export const PAGEBUILDER_SECRET_PREVIEW_SHARE = process.env.PAGEBUILDER_SECRET_PREVIEW_SHARE

export const API_HOST = process.env.API_HOST
export const API_PREFIX = '/d/api/'
export const API_FULL_PATH = `${API_HOST}${API_PREFIX}`

export const GRAPHENE_HOST = API_HOST
export const GRAPHENE_PREFIX = '/d/graphql/'
export const GRAPEHENE_GET_PREFIX = '/d/graphql-get/'
export const GRAPHENE_FULL_PATH = `${GRAPHENE_HOST}${GRAPHENE_PREFIX}`
export const GRAPHENE_GET_FULL_PATH = `${GRAPHENE_HOST}${GRAPEHENE_GET_PREFIX}`

export const IS_EXPORT = process.env.EXPORT === 'true'

export const api = axios.create({
  baseURL: API_FULL_PATH,
  withCredentials: true,
  withXSRFToken: true,
})

export const getBaseHostUrl = (locale): string => {
  const host = sites[locale]
  const port = BACKEND_PORT ? ':' + BACKEND_PORT : ''
  const url = `${PROTOCOL_SCHEMA}://${host}${port}`
  return url
}

export const getGQLHostUrl = (locale): string => {
  const baseHostUrl = getBaseHostUrl(locale)
  const url = `${baseHostUrl}${GRAPHENE_PREFIX}`
  return url
}

export const getGQLHostGetUrl = (locale): string => {
  const baseHostUrl = getBaseHostUrl(locale)
  const url = `${baseHostUrl}${GRAPEHENE_GET_PREFIX}`
  return url
}

export const getAPIUrl = (locale): string => {
  const baseHostUrl = getBaseHostUrl(locale)
  const url = `${baseHostUrl}${API_PREFIX}`
  return url
}

export const getAPIUrlCustom = (locale, prefix = ''): string => {
  const baseHostUrl = getBaseHostUrl(locale)
  const url = `${baseHostUrl}${prefix}`
  return url
}

export const getAxiosTranslated = (baseUrl, pagebuilder = false) => {
  let headers = {}
  if (pagebuilder) {
    headers = PAGEBUILDER_ADMIN_ENABLE ? { Authorization: PAGEBUILDER_SECRET_PREVIEW_SHARE } : {}
  }
  const apiCustom = axios.create({
    baseURL: baseUrl,
    withCredentials: true,
    withXSRFToken: true,
    headers: headers,
  })
  apiCustom.defaults.xsrfCookieName = 'csrftoken'
  apiCustom.defaults.xsrfHeaderName = 'X-CSRFTOKEN'
  return apiCustom
}

export const grapheneTranslated = (locale) => {
  const baseHostUrl = getGQLHostUrl(locale)
  const axiosInstance = getAxiosTranslated(baseHostUrl)
  return axiosInstance
}

export const grapheneGetTranslated = (locale) => {
  const baseHostUrl = getGQLHostGetUrl(locale)
  const axiosInstance = getAxiosTranslated(baseHostUrl)
  return axiosInstance
}

export const apiTranslated = (locale, pagebuilder = false) => {
  const apiPath = getAPIUrl(locale)
  const axiosInstance = getAxiosTranslated(apiPath, pagebuilder)
  return axiosInstance
}

export const apiTranslatedBase = (locale, pagebuilder = false) => {
  const apiPath = getAPIUrlCustom(locale, '/d/')
  const axiosInstance = getAxiosTranslated(apiPath, pagebuilder)
  return axiosInstance
}

export const apiTranslatedPagebuilder = (locale, pagebuilder = false) => {
  const apiPath = getAPIUrl(locale) + 'react-pagebuilder/'
  const axiosInstance = getAxiosTranslated(apiPath, pagebuilder)
  return axiosInstance
}

export const apiTranslatedAdmin = (locale, pagebuilder = false) => {
  const apiPath = getAPIUrlCustom(locale, '/d/admin/api/')
  const axiosInstance = getAxiosTranslated(apiPath, pagebuilder)
  return axiosInstance
}

export const graphene = axios.create({
  baseURL: GRAPHENE_FULL_PATH,
  withCredentials: true,
  withXSRFToken: true,
})

export const grapheneGet = axios.create({
  baseURL: GRAPHENE_GET_FULL_PATH,
})

export const NEWRELIC_ACCOUNT_ID: string = process.env.NEXT_PUBLIC_NEWRELIC_ACCOUNT_ID
  ? process.env.NEXT_PUBLIC_NEWRELIC_ACCOUNT_ID
  : ''
export const NEWRELIC_TRUST_KEY: string = process.env.NEXT_PUBLIC_NEWRELIC_TRUST_KEY
  ? process.env.NEXT_PUBLIC_NEWRELIC_TRUST_KEY
  : ''
export const NEWRELIC_AGENT_ID: string = process.env.NEXT_PUBLIC_NEWRELIC_AGENT_ID
  ? process.env.NEXT_PUBLIC_NEWRELIC_AGENT_ID
  : ''
export const NEWRELIC_LICENSE_KEY: string = process.env.NEXT_PUBLIC_NEWRELIC_LICENSE_KEY
  ? process.env.NEXT_PUBLIC_NEWRELIC_LICENSE_KEY
  : ''
export const NEWRELIC_APPLICATION_ID: string = process.env.NEXT_PUBLIC_NEWRELIC_APPLICATION_ID
  ? process.env.NEXT_PUBLIC_NEWRELIC_APPLICATION_ID
  : ''

export const isEmpty = (obj: any) => Object.keys(obj).length === 0 && obj.constructor === Object

export const toggle = (array: any, value: any) => {
  const index = array.indexOf(value)

  if (index === -1) {
    array.push(value)
  } else {
    array.splice(index, 1)
  }
}

export function escape(s: String) {
  return s
    ? s
        .replace(/&amp;/g, '&')
        .replace(/&#39;/g, "'")
        .replace(/&quot;/g, '"')
    : s
}

export const toAltTitle = (term: string) =>
  term ? `${term} | ${BRAND}`.replace(/  +/g, ' ') : term === '' ? '' : BRAND // Se l'alt è volutamente lasciato vuoto, è un'immagine decorativa quindi va lasciato vuoto

export const getYearOptions = (yearFrom: number, yearNumber: number) =>
  [...Array.from({ length: yearNumber }).keys()].map((i) => ({
    label: (i + yearFrom).toString(),
    value: (i + yearFrom).toString(),
  }))

export const getAssetsUrl = (url) =>
  process.env.STORYBOOK ? `http://localhost:3000/${url}` : `/${url}`

export const getImageUrl = (url) => getAssetsUrl(`images/${url}`)

export const getVideoUrl = (url) => (process.env.NODE_ENV === 'development' ? API_HOST + url : url)

export const numberNotation = (valore: any, numeroDecimali: number) => {
  const valoreSafe = Number.parseFloat(valore) || 0
  const [intPart, decimals] = valoreSafe.toFixed(numeroDecimali).toString().split('.')
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + (numeroDecimali > 0 ? `,${decimals}` : '')
}

// Lo distinguo da numberNotation, non si sa mai che i prezzi possano avere un formato diverso successivamente (es includere valuta)
export const priceNotation = (valore: any) => `� ${numberNotation(valore, 2)}`

export const getFormFieldStatus = (formikProps: any, propName: string) =>
  formikProps.touched[propName] ? (formikProps.errors[propName] ? 'error' : 'success') : null

export const isExternalDomain = (url: string, currentDomain?: string): boolean => {
  try {
    const domain = new URL(url)
    const hostname = domain.hostname.replace('www.', '')
    // se è un altro locale (domain diverso) o non appartiene a INTERNAL_DOMAINS
    return !currentDomain.includes(hostname) || !INTERNAL_DOMAINS.includes(hostname)
  } catch (e) {
    return url?.startsWith('/d/')
  }
}

export const isFileDownloadUrl = (url: string | object | null): boolean => {
  if (!url || typeof url === 'object') return false

  try {
    const domain = new URL(url)
    const pathname = domain.pathname.split('?')?.[0]
    if (!pathname.includes('/')) return false
    return pathname?.includes('.')
  } catch (_) {
    const pathname = url.split('?')?.[0]
    return pathname.split('?')?.[0]?.includes('.')
  }
}

export function truncateString(str: string, num: number) {
  return typeof str === 'string' && str.length > num ? str.slice(0, num) + '...' : str
}

export function slugify(text: string) {
  if (!text) return ''
  return text
    .toString() // Cast to string
    .normalize('NFD') // Normalize to NFD Unicode form
    .replace(/[\u0300-\u036F]/g, '') // Remove diacritics
    .toLowerCase() // Convert to lowercase
    .trim() // Trim leading and trailing spaces
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/[^\w\-]+/g, '') // Remove all non-word chars
    .replace(/\-\-+/g, '-') // Replace multiple hyphens with a single hyphen
}

export const removeHTMLTags = (str) => {
  if (str === null || str === '') return ''
  else str = str?.toString()
  return str?.replace(/(<([^>]+)>)/gi, '')
}

export const isToolbarVisible = (utente) => {
  if (!utente) return false

  if (!(utente.isStaff && utente.showToolbar)) return false

  return true
}

export const splitParagraphs = (text) => {
  if (!text) return []
  if (!text.includes('</p>')) return [text]
  const splits = text.split('</p>')
  return splits
    .map((x) => x.trim())
    .filter((x) => !!x.length)
    .map((x) => (x.startsWith('<p') ? x + '</p>' : x))
}

interface GsqList {
  edges: {
    node: object
  }[]
}

export const removeEdgesAndNode = (gql_list: GsqList) => {
  return gql_list.edges.map((item) => {
    return { ...item.node }
  })
}

export const extractSubtitleDescription = (htmlString: string) => {
  if (!htmlString) {
    return { subtitle: '', description: '' }
  }
  const h2Match = htmlString.match(/<h2>(.*?)<\/h2>/)
  const pMatch = htmlString.match(/<p>(.*?)<\/p>/)

  const subtitle = h2Match ? `<h2>${h2Match[1]}</h2>` : ''
  const description = pMatch ? `<p>${pMatch[1]}</p>` : ''

  return { subtitle, description }
}

export function appendQuery(url?: string, params?: Record<string, string | number | undefined>) {
  if (!url) return url
  // Parse url
  const [base, q = ''] = url.split('?', 2)
  const current = new URLSearchParams(q)
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') current.set(k, String(v))
  })
  const query = current.toString()
  return query ? `${base}?${query}` : base
}
